import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo
} from "react";
import { jwtDecode } from "jwt-decode";

// --- Create Wallet Context ---
const WalletContext = createContext();

// --- Lazy load ethers helper functions ---
async function getProvider() {
  const { ethers } = await import("ethers");
  return new ethers.providers.Web3Provider(window.ethereum);
}

async function getEthers() {
  const { ethers } = await import("ethers");
  return ethers;
}

// --- Wallet Provider Component ---
export const SignedWalletProvider = ({ children, config }) => {
  // Wallet Connection State
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState(null);

  const [jwtToken, setJwtToken] = useState(() => {
    const storedToken = localStorage.getItem(config.jwtStorageKey);
    if (storedToken) {
      try {
        const decodedToken = jwtDecode(storedToken);
        // Check if token is expired
        if (decodedToken.exp * 1000 < Date.now()) {
          console.log("Stored JWT is expired, removing it.");
          localStorage.removeItem(config.jwtStorageKey);
          return null;
        }
        return storedToken;
      } catch (error) {
        console.error("Failed to decode stored JWT:", error);
        localStorage.removeItem(config.jwtStorageKey);
        return null;
      }
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [network, setNetwork] = useState(null);

  // --- Logout Function ---
  const logout = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAccount(null);
    setJwtToken(null);
    setError(null);
    setNetwork(null);
    localStorage.removeItem(config.jwtStorageKey);
    console.log("User logged out.");
  }, []);

  const login = useCallback(async () => {
    if (isLoading) return false; // Return false if already in progress

    setIsLoading(true);
    setError(null);

    if (!window.ethereum) {
      setError("MetaMask is not installed.");
      setIsLoading(false);
      return false; // Signal failure
    }

    try {
      // 1. Connect to wallet and get signer/account
      const ethersProvider = await getProvider();
      await ethersProvider.send("eth_requestAccounts", []);
      const ethersSigner = ethersProvider.getSigner();
      const accountAddress = await ethersSigner.getAddress();
      const networkInfo = await ethersProvider.getNetwork();
      
      setNetwork(networkInfo);
      setProvider(ethersProvider);
      setSigner(ethersSigner);
      setAccount(accountAddress);

      // 2. Get nonce from backend
      const nonceResponse = await fetch(`${config.apiBaseUrl}/get-nonce/${accountAddress}`);
      if (!nonceResponse.ok) throw new Error("Failed to get nonce from server.");
      const { message } = await nonceResponse.json();

      // 3. User signs the message
      const signature = await ethersSigner.signMessage(message);

      // 4. Send signature to backend for verification & get JWT
      const loginResponse = await fetch(`${config.apiBaseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: accountAddress, signature }),
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json();
        throw new Error(errorData.detail || "Login verification failed.");
      }

      const { access_token } = await loginResponse.json();

      // 5. Store JWT in state and local storage
      setJwtToken(access_token);
      localStorage.setItem(config.jwtStorageKey, access_token);

      // Signal that the login was successful.
      setIsLoading(false);
      return true; 

    } catch (err) {
      console.error("Login process failed:", err);
      setError(err.message || "An error occurred during login.");
      logout(); // Ensure we are logged out on failure
      setIsLoading(false);
      
      // Signal that the login failed.
      return false; 
    }
  }, [isLoading, logout]);

  // --- Event Handlers for MetaMask ---
  const handleAccountsChanged = useCallback((accounts) => {
    if (accounts.length === 0) {
      // User has disconnected their account
      console.log("MetaMask account disconnected.");
      logout();
    } else if (accounts[0].toLowerCase() !== account?.toLowerCase()) {
      // User has switched to a different account
      console.log("MetaMask account changed. Forcing re-login.");
      logout();
    }
  }, [account, logout]);

  const handleChainChanged = useCallback(() => {
    // Reloading the page is the simplest and most robust way to handle chain changes.
    // It ensures all state is correctly re-initialized for the new network.
    console.log("Network changed, reloading the page.");
    window.location.reload();
  }, []);

  // --- UPDATED useEffect for initial load ---
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      if (jwtToken) {
        const reConnect = async () => {
          try {
            const ethersProvider = await getProvider();
            
            // --- GET AND SET NETWORK INFO ON RECONNECT ---
            const networkInfo = await ethersProvider.getNetwork();
            setNetwork(networkInfo);

            const accounts = await ethersProvider.listAccounts();
            if (accounts.length > 0) {
              const signer = ethersProvider.getSigner();
              setProvider(ethersProvider);
              setSigner(signer);
              setAccount(accounts[0]);
            } else {
              logout();
            }
          } catch (e) {
            console.error("Error reconnecting on page load:", e);
            logout();
          }
        };
        reConnect();
      }

      return () => { /* ... cleanup ... */ };
    }
  }, [jwtToken, handleAccountsChanged, handleChainChanged, logout]);

  // --- REFACTORED Effect to set up and tear down event listeners ---
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      // If a valid, non-expired JWT exists, try to re-establish the wallet connection silently
      if (jwtToken) {
        const reConnect = async () => {
          try {
            const ethersProvider = await getProvider();
            const accounts = await ethersProvider.listAccounts();
            if (accounts.length > 0) {
              const signer = ethersProvider.getSigner();
              setProvider(ethersProvider);
              setSigner(signer);
              setAccount(accounts[0]);
            } else {
              // A valid JWT exists but the wallet is locked or disconnected.
              // We can let them stay "logged in" but without a signer.
              // Or, force a logout. Forcing logout is often safer.
              console.log("JWT present but wallet not connected. Logging out.");
              logout();
            }
          } catch (e) {
            console.error("Error reconnecting on page load:", e);
            logout();
          }
        };
        reConnect();
      }

      return () => {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
        window.ethereum.removeListener("chainChanged", handleChainChanged);
      };
    }
  }, [jwtToken, handleAccountsChanged, handleChainChanged, logout]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    provider,
    signer,
    account,
    network,
    jwtToken,
    isLoading,
    error,
    login,
    logout,
  }), [provider, signer, account, network, jwtToken, isLoading, error, login, logout]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// --- Custom Hook to Use Wallet Context ---
export const useWallet = () => {
  return useContext(WalletContext);
};